if (!customElements.get('product-gallery-modal')) {
  class ProductGalleryModal extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
      this._sliderUnsub = null;
      this._sliderInitialized = false;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._dialog = this.querySelector('dialog');
      this._zoomableMedias = [...this.querySelectorAll('.product-gallery-modal__media--zoom')];

      this._zoomableMedias.forEach(media => {
        media.addEventListener('click', this._onMediaClick.bind(this), { signal });
      });

      const track = this.querySelector('.slider__list');
      if (track) {
        let dragOrigin = null;
        const startDrag = (point) => { dragOrigin = point; };
        const moveDrag = (point) => {
          if (!dragOrigin) return;
          if (Math.abs(point.x - dragOrigin.x) > 5 || Math.abs(point.y - dragOrigin.y) > 5) {
            dragOrigin = null;
            this._clearZoom();
          }
        };
        const endDrag = () => { dragOrigin = null; };

        track.addEventListener('mousedown', (e) => startDrag({ x: e.clientX, y: e.clientY }), { signal });
        track.addEventListener('mousemove', (e) => moveDrag({ x: e.clientX, y: e.clientY }), { signal });
        track.addEventListener('mouseup', endDrag, { signal });
        track.addEventListener('mouseleave', endDrag, { signal });

        track.addEventListener('touchstart', (e) => {
          if (e.touches.length > 1) return;
          const touch = e.touches[0];
          if (touch) startDrag({ x: touch.clientX, y: touch.clientY });
        }, { signal, passive: true });
        track.addEventListener('touchmove', (e) => {
          if (e.touches.length > 1 || this._isNativeZoomed()) return;
          const touch = e.touches[0];
          if (touch) moveDrag({ x: touch.clientX, y: touch.clientY });
        }, { signal, passive: true });
        track.addEventListener('touchend', endDrag, { signal });
        track.addEventListener('touchcancel', endDrag, { signal });
      }

      this._dialog?.addEventListener('click', this._onDialogClick.bind(this), { signal });
      this._dialog?.addEventListener('click', this._onSliderNavClick.bind(this), { signal, capture: true });
      this._dialog?.addEventListener('keydown', (e) => { if (e.key === 'Escape') this._dialog.close(); }, { signal });
      this._dialog?.addEventListener('open-to-slide', this._onOpenToSlide.bind(this), { signal });
      this._dialog?.addEventListener('close', this._onDialogClose.bind(this), { signal });

      if (window.visualViewport) {
        const onVisualViewportChange = this._onVisualViewportChange.bind(this);
        window.visualViewport.addEventListener('resize', onVisualViewportChange, { signal });
        window.visualViewport.addEventListener('scroll', onVisualViewportChange, { signal });
        onVisualViewportChange();
      }

      this._sliderUnsub = theme.pubsub.subscribe(theme.pubsub.events.SLIDER_CHANGE, (data) => {
        const slider = this._dialog?.querySelector('theme-slider');
        if (slider && data.id === slider.id) this._clearZoom();
      });

      this._setupGalleryTriggers(signal);
    }

    _onSliderNavClick(event) {
      if (event.target.closest('.slider__button, .slider__counter__thumbnails .thumbnail')) {
        this._clearZoom();
      }
    }

    _setupGalleryTriggers(signal) {
      const sectionRoot = this.closest('[data-section-id]') || document;
      sectionRoot.addEventListener('click', this._onGalleryImageClick.bind(this), { signal });
    }

    _onGalleryImageClick(event) {
      const image = event.target.closest('.product-gallery__slide .product-media__image, .product-gallery__item .product-media__image');
      if (!image) return;
      const slider = image.closest('theme-slider');
      if (slider?.classList.contains('moving')) return;

      const slide = image.closest('[data-media-id]');
      const mediaId = slide?.dataset.mediaId;

      this._dialog?.showModal();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._dialog?.dispatchEvent(new CustomEvent('open-to-slide', { detail: { mediaId } }));
        });
      });
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._sliderUnsub?.();
    }

    _initSlider() {
      const slider = this._dialog?.querySelector('theme-slider');
      if (!slider) return;
      slider.setUpSlider?.();
      this._sliderInitialized = true;
    }

    _setMediaZoom() {
      this._zoomableMedias.forEach(media => {
        const parent = media.closest('.product-gallery-modal__media-wrapper');
        if (!parent) return;
        const mediaRatio = parseFloat(parent.dataset.ratio) || 1;
        const { width, height } = parent.getBoundingClientRect();
        const containerRatio = width / height;
        media.classList.remove('vertical-zoom', 'horizontal-zoom');
        if (mediaRatio < containerRatio) {
          media.classList.add('vertical-zoom');
        } else if (mediaRatio > containerRatio) {
          media.classList.add('horizontal-zoom');
        }
      });
    }

    _onMediaClick(event) {
      if (!window.matchMedia('(min-width: 1024px)').matches) return;
      const media = event.currentTarget;
      const wrapper = media.closest('.product-gallery-modal__media-wrapper');
      const slide = media.closest('.product-gallery-modal__slide');
      if (!wrapper || !slide) return;
      const slideRect = slide.getBoundingClientRect();
      const horizontalPosition = (event.clientX - slideRect.left) / slideRect.width;
      const verticalPosition = (event.clientY - slideRect.top) / slideRect.height;
      wrapper.classList.toggle('media-zoom');
      if (wrapper.classList.contains('media-zoom')) {
        requestAnimationFrame(() => {
          slide.scrollTo({
            left: (slide.scrollWidth - slideRect.width) * horizontalPosition,
            top: (slide.scrollHeight - slideRect.height) * verticalPosition
          });
        });
      }
    }

    _clearZoom() {
      this._dialog?.querySelectorAll('.product-gallery-modal__media-wrapper.media-zoom').forEach(w => {
        w.classList.remove('media-zoom');
      });
      this._dialog?.querySelectorAll('.product-gallery-modal__slide').forEach(slide => {
        slide.scrollTo({ left: 0, top: 0 });
      });
      this._dialog?.classList.remove('native-zoom');
    }

    _isNativeZoomed() {
      if (window.matchMedia('(min-width: 1024px)').matches) return false;
      return !!(window.visualViewport && window.visualViewport.scale > 1.01);
    }

    _onVisualViewportChange() {
      this._dialog?.classList.toggle('native-zoom', this._isNativeZoomed());
    }

    _onDialogClick(event) {
      if (this._isNativeZoomed()) return;
      if (event.target === this._dialog) {
        this._dialog.close();
        return;
      }
      const slide = event.target.closest('.product-gallery-modal__slide');
      if (slide && !event.target.closest('.product-gallery-modal__media--zoom, .product-gallery-modal__video, .product-gallery-modal__iframe, .product-gallery-modal__model, .video-facade, .slider__button, .product-gallery-modal__close')) {
        this._dialog.close();
      }
    }

    _onOpenToSlide(event) {
      requestAnimationFrame(() => {
        if (!this._sliderInitialized) this._initSlider();

        this._clearZoom();
        this._setMediaZoom();

        const mediaId = event.detail?.mediaId;
        if (!mediaId) return;
        const slider = this._dialog?.querySelector('theme-slider');
        if (!slider) return;
        const slide = this._dialog?.querySelector(`.product-gallery-modal__slide[data-media-id="${mediaId}"]`);
        if (!slide) return;
        const index = parseInt(slide.dataset.index, 10);
        if (Number.isNaN(index)) return;
        this._navigateOnceLaidOut(slider, index);
      });
    }

    // The dialog's real geometry isn't guaranteed to be settled by the time
    // showModal() returns (a fixed rAF count was proving unreliable — the
    // slider was sometimes still reading zero/near-zero dimensions from the
    // dialog, positioning the target slide thousands of px off-canvas).
    // Poll per-frame until the slider actually has real width, then recompute
    // its geometry and navigate, instead of guessing a frame count.
    _navigateOnceLaidOut(slider, index, attempt) {
      attempt = attempt || 0;
      if (slider.offsetWidth > 0 || attempt >= 20) {
        slider.reanchor?.();
        slider.navigateToSlide(index, false);
        return;
      }
      requestAnimationFrame(() => this._navigateOnceLaidOut(slider, index, attempt + 1));
    }

    _onDialogClose() {
      this._sliderInitialized = false;
      this._clearZoom();
    }
  }

  customElements.define('product-gallery-modal', ProductGalleryModal);
}
